import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { scorecardKeys } from "./queryKeys";
import {
	fetchCandidateScorecard,
	fetchEvaluatorName,
	saveCandidateScorecard,
} from "@/integrations/supabase/scorecardApi";
import type { CandidateScorecardRecord, ScorecardData } from "@/types/scorecard";

/** Saved scorecard answers + completion metadata for one candidate. */
export function useCandidateScorecard(
	candidateId: string | undefined,
	organizationId: string | undefined,
) {
	return useQuery({
		queryKey: scorecardKeys.candidate(candidateId || "none"),
		queryFn: () => fetchCandidateScorecard(candidateId!, organizationId!),
		enabled: !!candidateId && !!organizationId,
		staleTime: 30 * 1000,
	});
}

/** Name of the evaluator that last saved the scorecard (metadata display only). */
export function useEvaluatorName(userId: string | null | undefined) {
	return useQuery({
		queryKey: [...scorecardKeys.all, "evaluator", userId || "none"],
		queryFn: () => fetchEvaluatorName(userId!).catch(() => null),
		enabled: !!userId,
		staleTime: 10 * 60 * 1000,
	});
}

interface SaveScorecardInput {
	data: ScorecardData;
	completedBy: string;
}

/** Persists the full answer set; the cache is patched with the server row. */
export function useSaveCandidateScorecard(
	candidateId: string | undefined,
	organizationId: string | undefined,
) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ data, completedBy }: SaveScorecardInput) =>
			saveCandidateScorecard(candidateId!, organizationId!, data, completedBy),
		onSuccess: (record: CandidateScorecardRecord) => {
			queryClient.setQueryData(scorecardKeys.candidate(candidateId || "none"), record);
		},
	});
}
